import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, TYPE_LABELS, levelColor, scoreColor, MODE_LABELS, modeBadge } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Wand2, Trash2, Package, Filter, Zap, Target, DollarSign, Users,
  Layers, Rocket, CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ScoreBreakdown } from "@/components/ScoreBreakdown";

const CountPill = ({ n, count, setCount }) => (
  <button
    onClick={() => setCount(n)}
    className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
      count === n ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
    }`}
  >
    {n}
  </button>
);

export default function IdeaEngine() {
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [trigger, setTrigger] = useState("");
  const [count, setCount] = useState(6);
  const [mode, setMode] = useState("auto");
  const [generating, setGenerating] = useState(false);
  const [ideas, setIdeas] = useState([]);
  const [filterTheme, setFilterTheme] = useState("");
  const [building, setBuilding] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSocial, setBulkSocial] = useState(true);
  const [bulkJob, setBulkJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const bulkBuild = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try {
      const { data } = await api.post("/products/bulk-build", { idea_ids: ids, queue_social: bulkSocial });
      toast.success(`Building ${ids.length} packages in the background…`);
      setSelectedIds(new Set());
      setBulkJob({ id: data.job_id, status: "running", completed: 0, total: ids.length });
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/jobs/${data.job_id}`);
          setBulkJob(j);
          if (["done", "error", "stopped"].includes(j.status)) {
            clearInterval(pollRef.current);
            if (j.status === "done") toast.success(`Built ${j.result?.built || 0} packages`);
            else if (j.status === "stopped") toast.warning("Stopped — cost limit reached");
            loadIdeas();
          }
        } catch { clearInterval(pollRef.current); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk build failed");
    }
  };

  const loadIdeas = () => {
    const q = filterTheme ? `?theme=${filterTheme}` : "";
    api.get(`/ideas${q}`).then(({ data }) => setIdeas(data));
  };

  useEffect(() => {
    api.get("/themes").then(({ data }) => { setThemes(data); if (!selected) setSelected(data[0]); });
  }, []);
  useEffect(() => { loadIdeas(); }, [filterTheme]);

  const generate = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/ideas/generate", { theme: selected.key, trigger, count, mode });
      toast.success(`Generated ${data.length} concepts for ${selected.name}`);
      setFilterTheme("");
      loadIdeas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const buildPackage = async (idea) => {
    setBuilding(idea.id);
    toast.info("Generating full listing package + mockup… this takes ~20s");
    try {
      const { data } = await api.post("/products/build", { idea_id: idea.id, generate_mockup: true });
      toast.success("Package ready — opening product");
      navigate(`/products?id=${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Build failed");
    } finally {
      setBuilding(null);
    }
  };

  const removeIdea = async (id) => {
    await api.delete(`/ideas/${id}`);
    setIdeas((prev) => prev.filter((i) => i.id !== id));
    setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
  };

  const allSelected = ideas.length > 0 && ideas.every((i) => selectedIds.has(i.id));
  const buildableCount = ideas.filter((i) => i.status !== "built").length;
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(ideas.map((i) => i.id)));
  };

  const deleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} concept${ids.length > 1 ? "s" : ""}? Built products are not affected.`)) return;
    try {
      const { data } = await api.post("/ideas/delete-bulk", { ids });
      toast.success(`Deleted ${data.deleted} concept${data.deleted === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      loadIdeas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const clearAll = async () => {
    const scope = filterTheme ? themes.find((t) => t.key === filterTheme)?.name || "this theme" : "ALL themes";
    if (!window.confirm(`Clear every concept in ${scope}? This removes ${ideas.length} idea${ideas.length === 1 ? "" : "s"}. Built products are not affected.`)) return;
    try {
      const { data } = await api.post("/ideas/delete-bulk", { all: true, ...(filterTheme ? { theme: filterTheme } : {}) });
      toast.success(`Cleared ${data.deleted} concept${data.deleted === 1 ? "" : "s"}`);
      setSelectedIds(new Set());
      loadIdeas();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="idea-engine-page">
      <div className="mb-6">
        <p className="overline text-blue-600">Emotion-driven Idea Engine</p>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Generate product concepts</h1>
        <p className="text-sm text-zinc-500 mt-1">Pick an emotional trigger — get scored, giftable POD concepts ready to build.</p>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        {/* Generation panel */}
        <div className="bg-white border border-zinc-200 rounded-lg p-5 h-fit lg:sticky lg:top-24">
          <div className="overline text-zinc-400 mb-3">1 · Choose an emotional theme</div>
          <div className="grid grid-cols-2 gap-2">
            {themes.map((t) => (
              <button
                key={t.key}
                onClick={() => setSelected(t)}
                data-testid={`theme-${t.key}`}
                className={`text-left p-3 rounded-md border transition-all duration-200 ${
                  selected?.key === t.key
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                    : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
                }`}
              >
                <div className="text-lg leading-none">{t.emoji}</div>
                <div className="text-xs font-semibold mt-1.5 leading-tight">{t.name}</div>
              </button>
            ))}
          </div>

          {selected && (
            <>
              <div className="overline text-zinc-400 mt-5 mb-2">2 · Specific trigger (optional)</div>
              <Input
                value={trigger}
                onChange={(e) => setTrigger(e.target.value)}
                placeholder={selected.triggers?.[0] || "e.g. pet memorial"}
                data-testid="trigger-input"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {selected.triggers?.map((tr) => (
                  <button
                    key={tr}
                    onClick={() => setTrigger(tr)}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-colors"
                  >
                    {tr}
                  </button>
                ))}
              </div>

              <div className="overline text-zinc-400 mt-5 mb-2">3 · How many concepts</div>
              <div className="flex gap-2">
                {[4, 6, 8, 10].map((n) => <CountPill key={n} n={n} count={count} setCount={setCount} />)}
              </div>

              <div className="overline text-zinc-400 mt-5 mb-2">4 · Product mode</div>
              <div className="grid grid-cols-3 gap-1.5">
                {[["auto", "Auto"], ["personalized", "Personalized"], ["ready_made", "Ready-made"]].map(([k, l]) => (
                  <button key={k} onClick={() => setMode(k)} data-testid={`mode-${k}`}
                    className={`text-[11px] py-1.5 rounded-md border transition-colors ${mode === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300"}`}>
                    {l}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-zinc-400 mt-1.5">
                {mode === "auto" ? "AI decides per concept whether buyer input is needed." : mode === "personalized" ? "All concepts require buyer input (name/date/photo…)." : "All concepts are finished designs — no buyer input."}
              </p>

              <button
                onClick={generate}
                disabled={generating}
                data-testid="generate-ideas-btn"
                className="w-full mt-5 h-11 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-medium text-sm flex items-center justify-center gap-2 transition-colors"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {generating ? "Generating…" : `Generate ${count} concepts`}
              </button>
            </>
          )}
        </div>

        {/* Ideas list */}
        <div>
          {bulkJob && bulkJob.status === "running" && (
            <div className="bg-white border border-blue-200 rounded-lg p-3 mb-3 flex items-center gap-3" data-testid="bulk-job-panel">
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              <span className="text-sm font-medium">Building packages…</span>
              <span className="text-xs text-zinc-500 ml-auto">{bulkJob.completed}/{bulkJob.total}</span>
            </div>
          )}
          {selectedIds.size > 0 && (
            <div className="bg-zinc-900 text-white rounded-lg p-3 mb-3 flex items-center gap-3 flex-wrap" data-testid="bulk-bar">
              <Layers className="w-4 h-4" />
              <span className="text-sm font-medium">{selectedIds.size} selected</span>
              <label className="flex items-center gap-2 text-xs ml-2">
                <Switch checked={bulkSocial} onCheckedChange={setBulkSocial} data-testid="bulk-social-toggle" />
                Auto-queue social
              </label>
              <div className="flex items-center gap-2 ml-auto">
                <button onClick={() => setSelectedIds(new Set())} className="text-xs text-zinc-400 hover:text-white">Clear</button>
                <button onClick={deleteSelected} data-testid="bulk-delete-btn" className="h-8 px-3 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Delete {selectedIds.size}
                </button>
                <button onClick={bulkBuild} data-testid="bulk-build-btn" className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium flex items-center gap-1.5">
                  <Rocket className="w-3.5 h-3.5" /> Build {selectedIds.size} packages
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <select
                  value={filterTheme}
                  onChange={(e) => setFilterTheme(e.target.value)}
                  data-testid="idea-theme-filter"
                  className="bg-white border border-zinc-200 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="">All themes</option>
                  {themes.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
                </select>
              </div>
              {ideas.length > 0 && (
                <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="select-all-ideas">
                  <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} />
                  <span className="text-xs text-zinc-500">Select all</span>
                </label>
              )}
            </div>
            <div className="flex items-center gap-3">
              {ideas.length > 0 && (
                <button onClick={clearAll} data-testid="clear-all-ideas-btn"
                  className="text-xs font-medium text-rose-600 hover:text-rose-700 flex items-center gap-1">
                  <Trash2 className="w-3.5 h-3.5" /> Clear all
                </button>
              )}
              <span className="text-xs text-zinc-400" data-testid="ideas-count">
              {ideas.length} concept{ideas.length === 1 ? "" : "s"}
              {buildableCount > 0 && <span className="text-emerald-600 font-medium"> · {buildableCount} ready to build</span>}
            </span>
            </div>
          </div>

          {ideas.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-lg p-16 text-center">
              <Sparkles className="w-8 h-8 text-zinc-300 mx-auto" />
              <p className="text-sm text-zinc-500 mt-3">No concepts yet. Pick a theme and generate your first batch.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-3">
              {ideas.map((idea) => (
                <div key={idea.id} data-testid={`idea-card-${idea.id}`} className="bg-white border border-zinc-200 rounded-lg p-4 hover:border-zinc-300 transition-colors flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={selectedIds.has(idea.id)} onCheckedChange={() => toggleSelect(idea.id)} data-testid={`select-idea-${idea.id}`} />
                      <Badge variant="secondary" className="text-[10px] font-medium">{TYPE_LABELS[idea.product_type] || idea.product_type}</Badge>
                      {idea.mode && <Badge className={`text-[10px] border ${modeBadge[idea.mode]} hover:opacity-100`}>{MODE_LABELS[idea.mode]}</Badge>}
                      {idea.status === "built" && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Built</Badge>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`font-display text-xl font-bold leading-none ${scoreColor(idea.impulse_score)}`}>{idea.impulse_score}</div>
                      <div className="text-[9px] text-zinc-400 uppercase tracking-wide">impulse</div>
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-[15px] tracking-tight mt-2 leading-snug">{idea.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed flex-1">{idea.concept}</p>

                  <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
                    <div className="flex items-center gap-1"><Target className="w-3 h-3 text-zinc-400" /><span className={levelColor(idea.competition)}>{idea.competition} comp</span></div>
                    <div className="flex items-center gap-1"><DollarSign className="w-3 h-3 text-zinc-400" /><span className={levelColor(idea.margin)}>{idea.margin} margin</span></div>
                    <div className="flex items-center gap-1"><Zap className="w-3 h-3 text-zinc-400" /><span className="text-zinc-500 truncate">{idea.seasonality}</span></div>
                  </div>
                  {idea.trigger && (
                    <div className="flex items-center gap-1 mt-2 text-[11px] text-zinc-400">
                      <Users className="w-3 h-3" /> {idea.trigger}
                    </div>
                  )}

                  <details className="mt-2 group">
                    <summary className="text-[11px] text-blue-600 hover:text-blue-700 cursor-pointer select-none" data-testid={`why-${idea.id}`}>Why {idea.impulse_score}?</summary>
                    <div className="mt-2"><ScoreBreakdown scores={idea.scores} /></div>
                  </details>

                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
                    <button
                      onClick={() => buildPackage(idea)}
                      disabled={building === idea.id}
                      data-testid={`build-package-${idea.id}`}
                      className="flex-1 h-9 rounded-md bg-zinc-900 hover:bg-zinc-800 disabled:opacity-60 text-white text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
                    >
                      {building === idea.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                      {building === idea.id ? "Building…" : "Build full package"}
                    </button>
                    <button onClick={() => removeIdea(idea.id)} data-testid={`delete-idea-${idea.id}`} className="h-9 w-9 rounded-md border border-zinc-200 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600 text-zinc-400 flex items-center justify-center transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
